import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  TextField,
} from "@mui/material";
import { useRecordContext } from "react-admin";
import { Student } from "../providers/studentsProvider";
import { useState } from "react";
import { setStudentStatus } from "../api/backoffice.api";
import type { StudentStatus } from "../model/StudentStatus";
import { grants } from "../backoffice.access_control";
import { LoadingButton } from "@mui/lab";
import { DialogCloseButton } from "../misc/DialogCloseButton";

export function ChangeStudentStatusDialog(props: { open: boolean; onClose: () => void }) {
  const { open, onClose } = props;
  const student = useRecordContext<Student>();
  // The status "completed" is not included here, because this is a derived state, which is not stored
  // in Firestore but calculated by the studentProvider.
  const [newStatus, setNewStatus] = useState<Exclude<StudentStatus, "completed"> | null>(null);
  const [reason, setReason] = useState<string>("");
  const [highlightError, setHighlightError] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setHighlightError(true);
    if (student.status === "completed") {
      if (newStatus === "active") {
        showErrorToast(
          'Der Status eines Fahrschülers, der alle Fahrprüfungen bestanden hat, kann nicht auf "Aktiv" gesetzt werden.',
        );
        return;
      } else if (newStatus === "cancelled") {
        showErrorToast(
          'Der Status eines Fahrschülers, der alle Fahrprüfungen bestanden hat, kann nicht auf "Abgebrochen" gesetzt werden.',
        );
        return;
      } else if (newStatus === "inactive") {
        showErrorToast(
          'Der Status eines Fahrschülers, der alle Fahrprüfungen bestanden hat, kann nicht auf "Inaktiv" gesetzt werden.',
        );
        return;
      } else if (newStatus === "onHold") {
        showErrorToast(
          'Der Status eines Fahrschülers, der alle Fahrprüfungen bestanden hat, kann nicht auf "Pausiert" gesetzt werden.',
        );
        return;
      }
    }
    if (student.status === "outstandingPayments" && !grants.has("changeStatusOfStudentWithOutstandingPayments")) {
      showErrorToast("Du bist nicht berechtigt, den Status eines Fahrschülers mit ausstehenden Zahlungen zu ändern.");
      return;
    }
    if (newStatus === "outstandingPayments" && !grants.has("blockStudent")) {
      showErrorToast('Du bist nicht berechtigt, den Status eines Fahrschülers auf "Offene Zahlungen" zu ändern.');
      return;
    }
    if (!reason.trim()) {
      showErrorToast("Bitte gib noch den Grund für die Statusänderung an.");
      return;
    }
    setSaving(true);
    await runSafely(() => setStudentStatus(student, newStatus!, reason), {
      errorMessage: `Fehler beim Ändern des Status von ${student.name}.`,
      successMessage: `Status von ${student.name} erfolgreich geändert.`,
      onSuccess: onClose,
    });
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Status von {student.name ?? "..."} ändern auf:</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <List>
          {(student.status !== "active" ||
            (grants.has("admin") && student.verboseStatus === "Aktiv (Grundbetrag offen)")) && (
            <ListItemButton selected={newStatus === "active"} disabled={saving} onClick={() => setNewStatus("active")}>
              <ListItemText>{grants.has("developer") ? "Aktiv (active)" : "Aktiv"}</ListItemText>
            </ListItemButton>
          )}
          {student.status !== "cancelled" && (
            <ListItemButton
              selected={newStatus === "cancelled"}
              disabled={saving}
              onClick={() => setNewStatus("cancelled")}
            >
              <ListItemText>{grants.has("developer") ? "Abgebrochen (cancelled)" : "Abgebrochen"}</ListItemText>
            </ListItemButton>
          )}
          {student.status !== "inactive" && (
            <ListItemButton
              selected={newStatus === "inactive"}
              disabled={saving}
              onClick={() => setNewStatus("inactive")}
            >
              <ListItemText>{grants.has("developer") ? "Inaktiv (inactive)" : "Inaktiv"}</ListItemText>
            </ListItemButton>
          )}
          {student.status !== "onHold" && (
            <ListItemButton selected={newStatus === "onHold"} disabled={saving} onClick={() => setNewStatus("onHold")}>
              <ListItemText>{grants.has("developer") ? "Pausiert (onHold)" : "Pausiert"}</ListItemText>
            </ListItemButton>
          )}
          {student.status !== "outstandingPayments" && grants.has("blockStudent") && (
            <ListItemButton
              selected={newStatus === "outstandingPayments"}
              disabled={saving}
              onClick={() => setNewStatus("outstandingPayments")}
            >
              <ListItemText>
                {grants.has("developer") ? "Offene Zahlungen (outstandingPayments)" : "Offene Zahlungen"}
              </ListItemText>
            </ListItemButton>
          )}
          <div style={{ height: "16px" }} />
          {newStatus && (
            <TextField
              type="text"
              label="Grund"
              fullWidth
              autoFocus
              disabled={saving}
              required
              error={highlightError && !reason.trim()}
              onChange={(event) => setReason(event.target.value)}
            />
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <LoadingButton variant="contained" loading={saving} disabled={!newStatus} onClick={save}>
          Speichern
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
